import React from 'react'
import { Send, Loader, Bot, User, Compass, Brain, Target, Sparkles } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { useJourneyStore } from '../store/journeyStore'
import toast from 'react-hot-toast'
import ReactMarkdown from 'react-markdown'

const JourneyPage = () => {
  const [message, setMessage] = React.useState('')
  const [isTyping, setIsTyping] = React.useState(false)
  const [isInitialized, setIsInitialized] = React.useState(false)
  const messagesEndRef = React.useRef(null)
  
  const { 
    messages, 
    currentSession, 
    currentPhase, 
    isLoading, 
    journeyProgress,
    startJourney, 
    sendMessage,
    loadJourneyStatus,
    loadConversationHistory
  } = useJourneyStore()

  // Simplified initialization - just load existing conversation history if it exists
  React.useEffect(() => {
    const initializeJourney = async () => {
      if (!isInitialized) {
        console.log('🚀 Initializing JourneyPage...')
        setIsInitialized(true)
        
        try {
          // Step 1: Check if user has an existing session
          const statusResult = await loadJourneyStatus()
          
          if (statusResult.success && currentSession) {
            console.log('📊 Found existing session:', currentSession)
            
            // Step 2: Load conversation history to display existing messages
            const historyResult = await loadConversationHistory()
            
            if (historyResult.success && historyResult.messages && historyResult.messages.length > 0) {
              console.log('📜 Loaded conversation history:', historyResult.messages.length, 'messages')
              // Messages are automatically loaded into store, no need for resume call
            } else {
              console.log('📝 No conversation history found')
            }
          } else {
            console.log('🆕 No existing session found - new user should start journey')
          }
        } catch (error) {
          console.error('❌ Initialization error:', error)
        }
      }
    }
    
    initializeJourney()
  }, [isInitialized, loadJourneyStatus, loadConversationHistory, currentSession])

  // Auto-scroll to bottom when new messages arrive
  React.useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  const handleStartJourney = async () => {
    console.log('🚀 Starting new journey...')
    const result = await startJourney()
    if (!result.success) {
      toast.error(result.error)
    }
  }

  const handleSendMessage = async (e) => {
    e.preventDefault()
    if (!message.trim() || isLoading) return

    const userMessage = message.trim()
    setMessage('')
    setIsTyping(true)

    const result = await sendMessage(userMessage)
    setIsTyping(false)
    
    if (!result.success) {
      toast.error(result.error)
    }
  }

  const getPhaseIcon = (phase) => {
    switch (phase) {
      case 'truth_anchor_analysis':
        return <Target className="w-4 h-4" />
      case 'expression_anchor_analysis':
        return <Sparkles className="w-4 h-4" />
      case 'attention_anchor_analysis':
        return <Brain className="w-4 h-4" />
      case 'evolution_anchor_analysis':
        return <Compass className="w-4 h-4" />
      default:
        return <Bot className="w-4 h-4" />
    }
  }

  const getPhaseColor = (phase) => {
    switch (phase) {
      case 'truth_anchor_analysis':
        return 'text-red-600'
      case 'expression_anchor_analysis':
        return 'text-blue-600'
      case 'attention_anchor_analysis':
        return 'text-amber-600'
      case 'evolution_anchor_analysis':
        return 'text-green-600'
      default:
        return 'text-primary-600'
    }
  }

  // Show start journey screen for new users or users without active sessions
  if (!currentSession && messages.length === 0 && !isLoading && isInitialized) {
    return (
      <div className="min-h-screen gradient-bg flex items-center justify-center p-4">
        <motion.div 
          className="max-w-2xl mx-auto text-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="card space-y-6">
            <div className="w-20 h-20 bg-gradient-to-br from-primary-500 to-accent-500 rounded-full flex items-center justify-center mx-auto mb-6">
              <Compass className="w-10 h-10 text-white" />
            </div>
            
            <h1 className="text-3xl font-bold gradient-text">
              Begin Your Brand Strategy Journey
            </h1>
            
            <p className="text-lg text-secondary-600">
              Embark on a philosophical exploration to discover the deeper human truths 
              your brand can serve. Through Socratic dialogue, we'll uncover the 
              foundational anchors that will differentiate your startup.
            </p>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 my-8">
              <div className="text-center p-4 bg-red-50 rounded-lg border border-red-200">
                <Target className="w-8 h-8 text-red-600 mx-auto mb-2" />
                <span className="text-sm font-medium text-red-800">Truth</span>
              </div>
              <div className="text-center p-4 bg-blue-50 rounded-lg border border-blue-200">
                <Sparkles className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                <span className="text-sm font-medium text-blue-800">Expression</span>
              </div>
              <div className="text-center p-4 bg-amber-50 rounded-lg border border-amber-200">
                <Brain className="w-8 h-8 text-amber-600 mx-auto mb-2" />
                <span className="text-sm font-medium text-amber-800">Attention</span>
              </div>
              <div className="text-center p-4 bg-green-50 rounded-lg border border-green-200">
                <Compass className="w-8 h-8 text-green-600 mx-auto mb-2" />
                <span className="text-sm font-medium text-green-800">Evolution</span>
              </div>
            </div>
            
            <button 
              onClick={handleStartJourney}
              disabled={isLoading}
              className="btn-primary text-lg px-8 py-4"
            >
              {isLoading ? (
                <>
                  <Loader className="loading-spinner mr-2" />
                  Initializing...
                </>
              ) : (
                <>
                  Start Journey
                  <Compass className="w-5 h-5 ml-2" />
                </>
              )}
            </button>
          </div>
        </motion.div>
      </div>
    )
  }

  // Show loading state during initialization
  if (!isInitialized) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <Loader className="loading-spinner w-8 h-8 mx-auto mb-4" />
          <p className="text-secondary-600">Loading your journey...</p>
        </div>
      </div>
    )
  }

  // Show conversation interface
  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Progress Bar */}
      <div className="bg-white border-b border-secondary-200 p-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between mb-2">
            <h1 className="text-xl font-semibold text-secondary-800">Brand Strategy Journey</h1>
            <span className="text-sm text-secondary-600">{journeyProgress}% Complete</span>
          </div>
          <div className="w-full bg-secondary-200 rounded-full h-2">
            <motion.div 
              className="bg-gradient-to-r from-primary-500 to-accent-500 h-2 rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${journeyProgress}%` }}
              transition={{ duration: 0.5 }}
            />
          </div>
          {currentPhase && (
            <div className="flex items-center mt-2">
              <span className={`${getPhaseColor(currentPhase)} mr-2`}>
                {getPhaseIcon(currentPhase)}
              </span>
              <span className="text-sm text-secondary-600 capitalize">
                {currentPhase?.replace(/_/g, ' ')}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Chat Messages */}
      <div className="flex-1 overflow-hidden flex flex-col max-w-4xl mx-auto w-full">
        <div className="flex-1 overflow-y-auto chat-container p-4 space-y-6">
          <AnimatePresence>
            {messages.map((msg, index) => (
              <motion.div
                key={msg.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`max-w-3xl w-full ${msg.role === 'user' ? 'chat-user' : 'chat-ai'}`}>
                  <div className="flex items-start space-x-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                      msg.role === 'user' 
                        ? 'bg-primary-500 text-white' 
                        : 'bg-secondary-100 text-secondary-600'
                    }`}>
                      {msg.role === 'user' ? (
                        <User className="w-4 h-4" />
                      ) : (
                        getPhaseIcon(msg.phase)
                      )}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2 mb-1">
                        <span className="text-sm font-medium text-secondary-800">
                          {msg.role === 'user' ? 'You' : msg.agent || 'Samvadic'}
                        </span>
                        <span className="text-xs text-secondary-500">
                          {new Date(msg.timestamp).toLocaleTimeString()}
                        </span>
                        {msg.phase && (
                          <span className={`text-xs px-2 py-1 rounded-full bg-opacity-10 ${getPhaseColor(msg.phase)}`}>
                            {msg.phase.replace(/_/g, ' ')}
                          </span>
                        )}
                      </div>
                      
                      <div className="prose prose-sm max-w-none text-secondary-700">
                        <ReactMarkdown>{msg.content}</ReactMarkdown>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>

          {/* Typing Indicator */}
          {(isLoading || isTyping) && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex justify-start"
            >
              <div className="chat-ai max-w-3xl">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 rounded-full bg-secondary-100 text-secondary-600 flex items-center justify-center">
                    <Bot className="w-4 h-4" />
                  </div>
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-secondary-400 rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-secondary-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                    <div className="w-2 h-2 bg-secondary-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Message Input */}
        <div className="border-t border-secondary-200 p-4">
          <form onSubmit={handleSendMessage} className="flex space-x-4">
            <div className="flex-1">
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault()
                    handleSendMessage(e)
                  }
                }}
                placeholder="Share your thoughts about your brand, business, or the question posed..."
                className="w-full input-field resize-none"
                rows={3}
                disabled={isLoading}
              />
            </div>
            <button
              type="submit"
              disabled={!message.trim() || isLoading}
              className="btn-primary px-6 py-3 flex items-center self-end"
            >
              {isLoading ? (
                <Loader className="loading-spinner" />
              ) : (
                <Send className="w-5 h-5" />
              )}
            </button>
          </form>
          
          <div className="mt-2 text-xs text-secondary-500 text-center">
            Press Enter to send, Shift+Enter for new line
          </div>
        </div>
      </div>
    </div>
  )
}

export default JourneyPage